/**
 * deepgramMicCapture - Minimal microphone → linear16 PCM capture for server STT.
 *
 * What it does: opens the microphone with `getUserMedia`, resamples the audio to 16 kHz
 * mono linear16 PCM (the format Deepgram streaming expects), and hands each chunk to a
 * caller-provided `onAudioData(ArrayBuffer)` callback.
 *
 * Why it exists: the Classic voice engine normally captures speech with the browser Web
 * Speech API. On Microsoft Edge that API is broken (errors at runtime, no transcript), so
 * the engine instead streams mic audio to `deepgramSTTService`. This module is the audio
 * source for that path. It is intentionally self-contained (no ChartMind / cross-feature
 * imports and no shared singleton) so the voice engine can own its own capture lifecycle.
 *
 * Invariant: callers must call `stop()` to release the microphone and AudioContext.
 *
 * See: STT_TTS_AND_VOICE_ARCHITECTURE.md (Microsoft Edge caveat)
 */

// Deepgram streaming wants 16 kHz mono linear16 PCM.
const TARGET_SAMPLE_RATE = 16000;
// ScriptProcessor buffer size (power of two); 4096 @ 48 kHz ≈ 85 ms per chunk.
const BUFFER_SIZE = 4096;

/**
 * Convert Float32 samples (-1..1) to Int16 linear16 PCM (-32768..32767).
 * @param {Float32Array} float32Array - Input samples.
 * @returns {Int16Array} linear16 PCM samples.
 */
function float32ToInt16(float32Array) {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return int16Array;
}

/**
 * Downsample mono float samples from the source rate to the target rate (linear interpolation).
 * @param {Float32Array} samples - Input samples at sourceSampleRate.
 * @param {number} sourceSampleRate - Original sample rate (e.g. 48000).
 * @param {number} targetSampleRate - Desired sample rate (e.g. 16000).
 * @returns {Float32Array} Resampled samples.
 */
function downsample(samples, sourceSampleRate, targetSampleRate) {
  if (sourceSampleRate === targetSampleRate) return samples;
  const ratio = sourceSampleRate / targetSampleRate;
  const newLength = Math.round(samples.length / ratio);
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio;
    const srcIndexFloor = Math.floor(srcIndex);
    const srcIndexCeil = Math.min(srcIndexFloor + 1, samples.length - 1);
    const fraction = srcIndex - srcIndexFloor;
    result[i] = samples[srcIndexFloor] * (1 - fraction) + samples[srcIndexCeil] * fraction;
  }
  return result;
}

/**
 * Create a microphone capture session that streams 16 kHz linear16 PCM to a callback.
 *
 * @param {Object} options
 * @param {(buffer: ArrayBuffer) => void} options.onAudioData - Called with each PCM chunk.
 *   The caller decides whether to forward it to the STT socket (e.g. skip while TTS plays).
 * @returns {{ start: () => Promise<void>, stop: () => void }}
 */
export function createDeepgramMicCapture({ onAudioData } = {}) {
  let audioContext = null;
  let source = null;
  let processor = null;
  let muteGain = null;
  let stream = null;
  let started = false;

  async function start() {
    if (started) return;

    // echoCancellation/noiseSuppression help keep TTS playback out of the STT stream.
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    audioContext = new AudioCtx();
    if (audioContext.state === 'suspended') {
      await audioContext.resume();
    }

    source = audioContext.createMediaStreamSource(stream);
    processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
    const sourceSampleRate = audioContext.sampleRate;

    processor.onaudioprocess = (event) => {
      if (!onAudioData) return;
      const input = event.inputBuffer.getChannelData(0);
      const resampled = downsample(input, sourceSampleRate, TARGET_SAMPLE_RATE);
      const pcm = float32ToInt16(resampled);
      onAudioData(pcm.buffer);
    };

    // A ScriptProcessor only runs while connected to the destination. Route it through a
    // zero-gain node so the captured audio is processed but never played back to the user.
    muteGain = audioContext.createGain();
    muteGain.gain.value = 0;
    source.connect(processor);
    processor.connect(muteGain);
    muteGain.connect(audioContext.destination);

    started = true;
  }

  function stop() {
    started = false;
    if (processor) {
      try {
        processor.disconnect();
        processor.onaudioprocess = null;
      } catch (e) {
        console.warn('[deepgramMicCapture] Error disconnecting processor:', e);
      }
      processor = null;
    }
    if (muteGain) {
      try {
        muteGain.disconnect();
      } catch (e) {
        console.warn('[deepgramMicCapture] Error disconnecting mute gain:', e);
      }
      muteGain = null;
    }
    if (source) {
      try {
        source.disconnect();
      } catch (e) {
        console.warn('[deepgramMicCapture] Error disconnecting source:', e);
      }
      source = null;
    }
    if (stream) {
      try {
        stream.getTracks().forEach((track) => track.stop());
      } catch (e) {
        console.warn('[deepgramMicCapture] Error stopping mic tracks:', e);
      }
      stream = null;
    }
    if (audioContext && audioContext.state !== 'closed') {
      try {
        audioContext.close();
      } catch (e) {
        console.warn('[deepgramMicCapture] Error closing AudioContext:', e);
      }
    }
    audioContext = null;
  }

  return { start, stop };
}

export default createDeepgramMicCapture;
